import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Badge, Button, Card, Col, Form, Modal, Row, Spinner } from 'react-bootstrap';
import { format, subDays } from 'date-fns';
import PageLayout from '../../layouts/PageLayout';
import { useBranch } from '../../context/BranchContext';
import { useAuth } from '../../context/AuthProvider';
import { cashierAPI } from '../../services';
import useFilterParams from '../../hooks/useFilterParams';
import InvalidParamsNotice from '../../components/InvalidParamsNotice';
import { toast } from 'react-toastify';

const formatCurrency = (value) => {
  const amount = Number(value || 0);
  return new Intl.NumberFormat('vi-VN', { style: 'currency', currency: 'VND', maximumFractionDigits: 0 }).format(
    amount,
  );
};

const differenceBadge = (value) => {
  if (value > 0) return 'success';
  if (value < 0) return 'danger';
  return 'secondary';
};

const defaultFilters = () => {
  const today = new Date();
  const start = subDays(today, 6);
  return {
    minDifference: 1000,
    fromDate: format(start, 'yyyy-MM-dd'),
    toDate: format(today, 'yyyy-MM-dd'),
  };
};

/**
 * #191439 — Màn này là ĐÍCH của link sâu trong 2 tin đối soát tiền (dòng 22, 23 của bảng khảo
 * sát #132320): cảnh báo chênh lệch tiền lớn của 1 ca, và tổng hợp đối soát cuối ngày. Tin
 * kèm `?branch_id=&from=&to=`, nên trang phải đọc được `from`/`to`; không đọc thì link mở ra
 * khoảng mặc định 7 ngày và người nhận vẫn phải tự chọn lại đúng ngày đang lệch.
 *
 * Tên `from`/`to` lấy nguyên ở `KHUON-CHUAN-URL-PARAM.md` mục 1. `branch_id` do `BranchContext`
 * lo nên KHÔNG khai trong spec này.
 */
const isIsoDate = (v) => /^\d{4}-\d{2}-\d{2}$/.test(v);

const CASHIER_TRACING_FILTER_SPEC = {
  from: { default: () => defaultFilters().fromDate, valid: isIsoDate },
  to: { default: () => defaultFilters().toDate, valid: isIsoDate },
  min_difference: {
    default: 1000,
    parse: Number,
    valid: (v) => /^\d+$/.test(v),
  },
};

const CashierTracingPage = () => {
  const { selectedBranch } = useBranch();
  const { user } = useAuth();

  const [urlFilters, setUrlFilters, , { ignored, dismissIgnored }] = useFilterParams(
    CASHIER_TRACING_FILTER_SPEC,
  );
  // Giữ nguyên hình dạng `filters` mà cả trang đang dùng — chỉ đổi NGUỒN của trạng thái.
  const filters = useMemo(
    () => ({
      minDifference: urlFilters.min_difference,
      fromDate: urlFilters.from,
      toDate: urlFilters.to,
    }),
    [urlFilters],
  );
  const branchId = selectedBranch?.id;

  const [pending, setPending] = useState([]);
  const [pendingLoading, setPendingLoading] = useState(false);

  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(false);

  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const [showReconcileModal, setShowReconcileModal] = useState(false);
  const [reconcileForm, setReconcileForm] = useState({
    explanation: '',
    action_taken: '',
    discrepancy_type: '',
    evidence_urls: '',
  });
  const [reconciling, setReconciling] = useState(false);

  const fetchPending = useCallback(async () => {
    try {
      setPendingLoading(true);
      const response = await cashierAPI.getPendingReconciliations({
        branch_id: branchId,
        min_difference: filters.minDifference || undefined,
      });
      const data = Array.isArray(response?.data)
        ? response.data
        : Array.isArray(response)
        ? response
        : response?.items || [];
      setPending(data);
      if (data.length) {
        setSelected((prev) => prev || data[0]);
      } else {
        setSelected(null);
        setDetail(null);
      }
    } catch (error) {
      console.error('Cannot load pending reconciliations', error);
      toast.error('Không tải được danh sách đối soát');
    } finally {
      setPendingLoading(false);
    }
  }, [branchId, filters.minDifference]);

  const fetchStats = useCallback(async () => {
    try {
      setStatsLoading(true);
      const response = await cashierAPI.getReconciliationStats({
        branch_id: branchId,
        from_date: filters.fromDate,
        to_date: filters.toDate,
      });
      setStats(response?.data || response);
    } catch (error) {
      console.error('Cannot load reconciliation stats', error);
    } finally {
      setStatsLoading(false);
    }
  }, [branchId, filters.fromDate, filters.toDate]);

  const fetchDetail = useCallback(async (item) => {
    if (!item?.shift_id) {
      setDetail(null);
      return;
    }
    try {
      setDetailLoading(true);
      const response = await cashierAPI.getReconciliationDetail(item.shift_id);
      setDetail(response?.data || response);
    } catch (error) {
      console.error('Cannot load reconciliation detail', error);
      toast.error('Không tải được chi tiết ca');
    } finally {
      setDetailLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPending();
    fetchStats();
  }, [fetchPending, fetchStats]);

  useEffect(() => {
    if (selected) {
      fetchDetail(selected);
    }
  }, [selected, fetchDetail]);

  const summary = useMemo(() => {
    if (!stats?.summary) return null;
    const { total_shifts, reconciled, pending: pendingCount, total_difference } = stats.summary;
    return { total_shifts, reconciled, pendingCount, total_difference };
  }, [stats]);

  // Tên trường trong trang -> tên param trên URL.
  const handleFilterChange = (field, value) => {
    const urlName = {
      minDifference: 'min_difference',
      fromDate: 'from',
      toDate: 'to',
    }[field];
    setUrlFilters({ [urlName]: value });
  };

  const handleOpenReconcileModal = () => {
    setShowReconcileModal(true);
    setReconcileForm({
      explanation: detail?.explanation || '',
      action_taken: detail?.action_taken || '',
      discrepancy_type: detail?.discrepancy_type || '',
      evidence_urls: (detail?.evidence_urls || []).join(', '),
    });
  };

  const handleReconcileSubmit = async (event) => {
    event.preventDefault();
    if (!user?.id) {
      toast.error('Không tìm thấy user hiện tại');
      return;
    }
    if (!detail?.shift_id) {
      toast.error('Thiếu thông tin ca');
      return;
    }
    const payload = {
      explanation: reconcileForm.explanation.trim(),
      action_taken: reconcileForm.action_taken.trim(),
      discrepancy_type: reconcileForm.discrepancy_type,
      evidence_urls: reconcileForm.evidence_urls
        .split(',')
        .map((url) => url.trim())
        .filter(Boolean),
    };
    try {
      setReconciling(true);
      await cashierAPI.reconcileShift(detail.shift_id, payload);
      toast.success('Đối soát thành công');
      setShowReconcileModal(false);
      fetchPending();
      fetchStats();
      fetchDetail({ shift_id: detail.shift_id });
    } catch (error) {
      console.error('Cannot reconcile shift', error);
    } finally {
      setReconciling(false);
    }
  };

  return (
    <PageLayout>
      <div className="mc-page">
        <InvalidParamsNotice
          ignored={ignored}
          onDismiss={dismissIgnored}
        />
        <div className="mc-page-header">
          <div className="mc-page-header-title">
            <h1>Tracing & đối soát chênh lệch</h1>
          </div>
          <div className="d-flex gap-2 flex-wrap align-items-center">
            <Form.Control
              type="number"
              min="0"
              style={{ width: 160 }}
              value={filters.minDifference}
              onChange={(e) => handleFilterChange('minDifference', Number(e.target.value))}
              placeholder="Min chênh lệch"
            />
            <Form.Control
              type="date"
              value={filters.fromDate}
              onChange={(e) => handleFilterChange('fromDate', e.target.value)}
            />
            <Form.Control
              type="date"
              value={filters.toDate}
              onChange={(e) => handleFilterChange('toDate', e.target.value)}
            />
            <Button
              variant="primary"
              onClick={() => {
                fetchPending();
                fetchStats();
              }}
              disabled={pendingLoading || statsLoading}
            >
              Làm mới
            </Button>
          </div>
        </div>

        <Row className="g-3 mb-4">
          <Col md={3}>
            <Card>
              <Card.Body>
                <small className="text-muted">Tổng số ca</small>
                <h4 className="mb-0">{summary?.total_shifts ?? '--'}</h4>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card>
              <Card.Body>
                <small className="text-muted">Đã đối soát</small>
                <h4 className="mb-0">{summary?.reconciled ?? '--'}</h4>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card>
              <Card.Body>
                <small className="text-muted">Đang chờ</small>
                <h4 className="mb-0">{summary?.pendingCount ?? '--'}</h4>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card>
              <Card.Body>
                <small className="text-muted">Tổng chênh lệch</small>
                <h4 className={`mb-0 text-${differenceBadge(summary?.total_difference)}`}>
                  {formatCurrency(summary?.total_difference || 0)}
                </h4>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        <Row className="g-3">
          <Col md={5}>
            <Card className="h-100">
              <Card.Header>
                <h5 className="mb-1">Ca cần xử lý</h5>
                <small className="text-muted">GET /reconciliation/pending</small>
              </Card.Header>
              <Card.Body className="p-0">
                {pendingLoading ? (
                  <div className="text-center py-4">
                    <Spinner
                      size="sm"
                      animation="border"
                    />{' '}
                    Đang tải...
                  </div>
                ) : pending.length === 0 ? (
                  <div className="text-center text-muted py-4">Không có ca cần đối soát</div>
                ) : (
                  <div className="list-group list-group-flush">
                    {pending.map((item) => (
                      <button
                        type="button"
                        key={item.id || item.shift_id}
                        className={`list-group-item list-group-item-action d-flex justify-content-between ${
                          selected?.shift_id === item.shift_id ? 'active' : ''
                        }`}
                        onClick={() => setSelected(item)}
                      >
                        <div>
                          <div className="fw-semibold">
                            {item.shift?.employee?.full_name || item.shift?.employee_name}
                          </div>
                          <small className="text-muted">
                            {item.shift?.branch?.name} ·{' '}
                            {format(new Date(item.created_at || item.createdAt), 'dd/MM HH:mm')}
                          </small>
                        </div>
                        <Badge bg={differenceBadge(item.difference)}>{formatCurrency(item.difference)}</Badge>
                      </button>
                    ))}
                  </div>
                )}
              </Card.Body>
            </Card>
          </Col>
          <Col md={7}>
            <Card className="h-100">
              <Card.Header className="d-flex justify-content-between align-items-center">
                <div>
                  <h5 className="mb-1">Chi tiết đối soát</h5>
                  <small className="text-muted">GET /reconciliation/:shiftId</small>
                </div>
                {detail && (
                  <Button
                    variant="primary"
                    onClick={handleOpenReconcileModal}
                  >
                    Ghi nhận đối soát
                  </Button>
                )}
              </Card.Header>
              <Card.Body>
                {detailLoading ? (
                  <div className="text-center py-4">
                    <Spinner
                      size="sm"
                      animation="border"
                    />{' '}
                    Đang tải chi tiết...
                  </div>
                ) : !detail ? (
                  <div className="text-muted">Chọn một ca để xem chi tiết.</div>
                ) : (
                  <>
                    <div className="mb-3">
                      <div className="fw-semibold">{detail.shift?.employee?.full_name}</div>
                      <small className="text-muted">
                        {detail.shift?.branch?.name} · {format(new Date(detail.shift?.start_time), 'dd/MM HH:mm')} -{' '}
                        {format(new Date(detail.shift?.end_time), 'dd/MM HH:mm')}
                      </small>
                    </div>
                    <Row className="g-3">
                      <Col md={6}>
                        <div className="border rounded p-3">
                          <div className="text-muted small">Opening</div>
                          <h5>{formatCurrency(detail.opening_cash)}</h5>
                        </div>
                      </Col>
                      <Col md={6}>
                        <div className="border rounded p-3">
                          <div className="text-muted small">Doanh thu tiền mặt</div>
                          <h5>{formatCurrency(detail.cash_revenue)}</h5>
                        </div>
                      </Col>
                      <Col md={6}>
                        <div className="border rounded p-3">
                          <div className="text-muted small">Phiếu chi</div>
                          <h5>{formatCurrency(detail.cash_expenses)}</h5>
                        </div>
                      </Col>
                      <Col md={6}>
                        <div className="border rounded p-3">
                          <div className="text-muted small">Dự kiến</div>
                          <h5>{formatCurrency(detail.expected_closing)}</h5>
                        </div>
                      </Col>
                    </Row>
                    <div className="mt-3 d-flex gap-4 align-items-center">
                      <div>
                        <div className="text-muted small">Thực tế</div>
                        <h4>{formatCurrency(detail.actual_closing)}</h4>
                      </div>
                      <div>
                        <div className="text-muted small">Chênh lệch</div>
                        <h4 className={`text-${differenceBadge(detail.difference)}`}>
                          {formatCurrency(detail.difference)}
                        </h4>
                      </div>
                    </div>
                    {detail.breakdown?.cash_orders && (
                      <div className="mt-4">
                        <h6>Đơn hàng tiền mặt ({detail.breakdown.cash_orders.order_count})</h6>
                        <div className="small text-muted">
                          Tổng: {formatCurrency(detail.breakdown.cash_orders.total)} · Hiển thị tối đa 5 dòng
                        </div>
                        <ul className="list-unstyled small mt-2 mb-0">
                          {(detail.breakdown.cash_orders.orders || []).slice(0, 5).map((order) => (
                            <li
                              key={order.order_number}
                              className="d-flex justify-content-between border-bottom py-1"
                            >
                              <span>{order.order_number}</span>
                              <span>{formatCurrency(order.amount)}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {detail.breakdown?.cash_vouchers && (
                      <div className="mt-4">
                        <h6>Phiếu chi ({detail.breakdown.cash_vouchers.voucher_count})</h6>
                        <div className="small text-muted">
                          Tổng: {formatCurrency(detail.breakdown.cash_vouchers.total)} · Hiển thị tối đa 5 dòng
                        </div>
                        <ul className="list-unstyled small mt-2 mb-0">
                          {(detail.breakdown.cash_vouchers.vouchers || []).slice(0, 5).map((voucher) => (
                            <li
                              key={voucher.voucher_number}
                              className="d-flex justify-content-between border-bottom py-1"
                            >
                              <span>{voucher.voucher_number}</span>
                              <span>{formatCurrency(voucher.amount)}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {detail.is_reconciled && (
                      <div className="alert alert-success mt-4 mb-0">
                        Đã đối soát bởi {detail.reconciler?.full_name} lúc{' '}
                        {detail.reconciled_at ? format(new Date(detail.reconciled_at), 'dd/MM HH:mm') : '-'}
                      </div>
                    )}
                  </>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>

        <Card className="mt-4">
          <Card.Header>
            <h5 className="mb-1">Nhắc nhở gần đây</h5>
            <small className="text-muted">Top unreconciled từ thống kê</small>
          </Card.Header>
          <Card.Body className="p-0">
            {statsLoading ? (
              <div className="text-center py-3">
                <Spinner
                  size="sm"
                  animation="border"
                />{' '}
                Đang tải thống kê...
              </div>
            ) : stats?.recent_unreconciled?.length ? (
              <div className="list-group list-group-flush">
                {stats.recent_unreconciled.map((item) => (
                  <div
                    className="list-group-item d-flex justify-content-between"
                    key={item.shift_id}
                  >
                    <div>
                      <div className="fw-semibold">{item.employee}</div>
                      <small className="text-muted">{format(new Date(item.shift_date), 'dd/MM HH:mm')}</small>
                    </div>
                    <Badge bg={differenceBadge(item.difference)}>{formatCurrency(item.difference)}</Badge>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center text-muted py-3">Không có dữ liệu</div>
            )}
          </Card.Body>
        </Card>
      </div>

      <Modal
        show={showReconcileModal}
        onHide={() => setShowReconcileModal(false)}
      >
        <Form onSubmit={handleReconcileSubmit}>
          <Modal.Header closeButton>
            <Modal.Title>Ghi nhận đối soát</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>Nguyên nhân *</Form.Label>
              <Form.Control
                type="text"
                value={reconcileForm.discrepancy_type}
                onChange={(e) => setReconcileForm((prev) => ({ ...prev, discrepancy_type: e.target.value }))}
                placeholder="Nhập nguyên nhân chênh lệch cụ thể..."
                required
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Giải trình *</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={reconcileForm.explanation}
                onChange={(e) => setReconcileForm((prev) => ({ ...prev, explanation: e.target.value }))}
                required
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Hành động khắc phục *</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                value={reconcileForm.action_taken}
                onChange={(e) => setReconcileForm((prev) => ({ ...prev, action_taken: e.target.value }))}
                required
              />
            </Form.Group>
            <Form.Group>
              <Form.Label>Evidence URLs (cách nhau bởi dấu phẩy)</Form.Label>
              <Form.Control
                value={reconcileForm.evidence_urls}
                onChange={(e) => setReconcileForm((prev) => ({ ...prev, evidence_urls: e.target.value }))}
                placeholder="https://..."
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="secondary"
              onClick={() => setShowReconcileModal(false)}
            >
              Hủy
            </Button>
            <Button
              type="submit"
              variant="primary"
              disabled={reconciling}
            >
              {reconciling ? 'Đang xử lý...' : 'Xác nhận đối soát'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </PageLayout>
  );
};

export default CashierTracingPage;
